import React, { useState, useRef, useEffect } from 'react';
import { Paperclip, Download, Reply, Send, X, CornerDownRight } from 'lucide-react';
import { API_URL } from '../../config/api';
import { uploadDocumentToCloudinary } from '../../config/cloudinary';

const getAuthHeaders = () => {
  const token = localStorage.getItem("access_token");
  const headers = { 'Content-Type': 'application/json' };
  if (token) headers['Authorization'] = `Bearer ${token}`;
  return headers;
};

const getUserDisplayName = (item) => {
  if (item.display_name) return item.display_name;
  if (item.user_name) return item.user_name;
  if (item.username) return item.username;
  if (item.name) return item.name;
  const firstName = item.first_name || '';
  const lastName = item.last_name || '';
  const fullName = `${firstName} ${lastName}`.trim();
  if (fullName) return fullName;
  if (item.email) return item.email.split('@')[0];
  return `User #${item.user_id}`;
};

const getInitials = (name) => {
  if (!name) return 'U';
  const parts = name.trim().split(' ').filter(Boolean);
  if (parts.length === 0) return 'U';
  if (parts.length === 1) return parts[0].substring(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};

const getFilenameFromUrl = (url) => {
  if (!url) return 'Document';
  try {
    const urlParts = url.split('/');
    const lastPart = urlParts[urlParts.length - 1];
    const filename = decodeURIComponent(lastPart.split('?')[0]);
    return filename || 'Document';
  } catch (error) {
    return 'Document';
  }
};

export default function Forum() {
  const [discussionText, setDiscussionText] = useState('');
  const [selectedSubject, setSelectedSubject] = useState('');
  const [courses, setCourses] = useState([]);
  const [loadingCourses, setLoadingCourses] = useState(true);
  const [discussions, setDiscussions] = useState([]);
  const [loadingPosts, setLoadingPosts] = useState(false);
  const [replyingTo, setReplyingTo] = useState(null);
  const [replyText, setReplyText] = useState('');
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploadingFile, setUploadingFile] = useState(false);

  const chatEndRef = useRef(null);
  const textareaRef = useRef(null);
  const scrollContainerRef = useRef(null);
  const fileInputRef = useRef(null);

  const handleDownload = async (url, fileName = 'document') => {
    try {
      if (!url) { alert('File URL is missing'); return; }
      const urlParts = url.split('/');
      const lastPart = urlParts[urlParts.length - 1];
      const extractedFileName = decodeURIComponent(lastPart.split('?')[0]) || fileName;
      let downloadUrl = url;
      if (url.includes('cloudinary.com')) {
        if (url.includes('/authenticated/')) downloadUrl = url.replace('/authenticated/', '/upload/');
        else if (url.includes('/private/')) downloadUrl = url.replace('/private/', '/upload/');
        if (downloadUrl.includes('/upload/')) downloadUrl = downloadUrl.replace('/upload/', '/upload/fl_attachment/');
      }
      try {
        const response = await fetch(downloadUrl, { method: 'GET', mode: 'cors' });
        if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
        const blob = await response.blob();
        const blobUrl = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = blobUrl; link.download = extractedFileName; link.style.display = 'none';
        document.body.appendChild(link); link.click();
        setTimeout(() => { document.body.removeChild(link); window.URL.revokeObjectURL(blobUrl); }, 100);
      } catch {
        const link = document.createElement('a');
        link.href = downloadUrl; link.download = extractedFileName;
        link.target = '_blank'; link.rel = 'noopener noreferrer'; link.style.display = 'none';
        document.body.appendChild(link); link.click();
        setTimeout(() => { document.body.removeChild(link); }, 100);
      }
    } catch (error) {
      console.error('❌ Download failed:', error);
      window.open(url, '_blank', 'noopener,noreferrer');
    }
  };

  const scrollToBottom = (behavior = 'smooth') => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollTo({ top: scrollContainerRef.current.scrollHeight, behavior });
    }
  };

  useEffect(() => { if (discussions.length > 0) scrollToBottom('auto'); }, [discussions.length]);
  useEffect(() => { if (replyingTo) setTimeout(() => scrollToBottom('smooth'), 100); }, [replyingTo]);

  useEffect(() => {
    const fetchCourses = async () => {
      try {
        const response = await fetch(`${API_URL}/courses/get_my_active_courses`, { headers: getAuthHeaders() });
        if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
        const data = await response.json();
        if (data?.success) setCourses(data.data || []);
      } catch (error) {
        console.error('Error fetching courses:', error);
      } finally {
        setLoadingCourses(false);
      }
    };
    fetchCourses();
  }, []);

  const flattenReplies = (replies, allReplies = [], seenIds = new Set()) => {
    replies.forEach(reply => {
      if (seenIds.has(reply.reply_id)) return;
      seenIds.add(reply.reply_id);
      allReplies.push({
        id: reply.reply_id,
        author: getUserDisplayName(reply),
        avatar_url: reply.avatar_url,
        content: reply.content,
        date: new Date(reply.created_at).toLocaleDateString('en-US', { month: 'short', day: '2-digit', year: 'numeric' }),
        replyTo: reply.parent_reply_id ? 'Previous Reply' : null,
        parentReplyId: reply.parent_reply_id
      });
      if (reply.replies?.length > 0) flattenReplies(reply.replies, allReplies, seenIds);
    });
    return allReplies;
  };

  const transformPosts = (data) =>
    (data || []).map(post => ({
      id: post.post_id,
      author: getUserDisplayName(post),
      avatar_url: post.avatar_url,
      subject: post.title || 'Discussion',
      date: new Date(post.created_at).toLocaleDateString('en-US', { month: 'short', day: '2-digit', year: 'numeric' }),
      content: post.content,
      document: post.attachment ? getFilenameFromUrl(post.attachment) : '',
      attachment_url: post.attachment,
      replies: flattenReplies(post.replies || [])
    })).reverse();

  useEffect(() => {
    const fetchPosts = async () => {
      if (!selectedSubject) { setDiscussions([]); return; }
      setLoadingPosts(true);
      try {
        const response = await fetch(`${API_URL}/discussion_forum/course/${selectedSubject}`);
        const data = await response.json();
        setDiscussions(transformPosts(data));
      } catch (error) {
        console.error('Error fetching posts:', error);
        setDiscussions([]);
      } finally {
        setLoadingPosts(false);
      }
    };
    fetchPosts();
  }, [selectedSubject]);

  const refreshPosts = async () => {
    const response = await fetch(`${API_URL}/discussion_forum/course/${selectedSubject}`);
    const data = await response.json();
    return transformPosts(data);
  };

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) { alert('File size should not exceed 10MB'); return; }
    setSelectedFile(file);
  };

  const handlePost = async () => {
    if ((!discussionText.trim() && !selectedFile) || !selectedSubject) return;
    try {
      setUploadingFile(true);
      let attachmentUrl = null;
      if (selectedFile) attachmentUrl = await uploadDocumentToCloudinary(selectedFile);
      const selectedCourse = courses.find((c) => c.course_id == selectedSubject);
      const payload = {
        course_id: parseInt(selectedSubject),
        title: selectedCourse?.title || 'Discussion',
        content: discussionText.trim() || 'Shared a file',
        attachment: attachmentUrl,
      };
      const response = await fetch(`${API_URL}/discussion_forum/create_post`, {
        method: 'POST', headers: getAuthHeaders(), body: JSON.stringify(payload)
      });
      if (response.ok) {
        setDiscussions(await refreshPosts());
        setDiscussionText(''); setSelectedFile(null);
        if (textareaRef.current) textareaRef.current.style.height = 'auto';
        if (fileInputRef.current) fileInputRef.current.value = '';
        setTimeout(() => scrollToBottom('smooth'), 100);
      }
    } catch (error) {
      console.error('Error creating post:', error);
      alert('Failed to upload file or create post. Please try again.');
    } finally {
      setUploadingFile(false);
    }
  };

  const handleReply = async (discussionId, replyToAuthor = null, parentReplyId = null) => {
    if (!replyText.trim()) return;
    try {
      const payload = {
        post_id: parseInt(discussionId),
        content: replyText,
        parent_reply_id: parentReplyId ? parseInt(parentReplyId) : null,
      };
      const response = await fetch(`${API_URL}/discussion_forum/create_reply`, {
        method: 'POST', headers: getAuthHeaders(), body: JSON.stringify(payload)
      });
      if (response.ok) {
        setDiscussions(await refreshPosts());
        setReplyText(''); setReplyingTo(null);
        setTimeout(() => scrollToBottom('smooth'), 100);
      }
    } catch (error) {
      console.error('Error creating reply:', error);
    }
  };

  const handleTextareaInput = (e) => {
    e.target.style.height = 'auto';
    e.target.style.height = Math.min(e.target.scrollHeight, 120) + 'px';
  };

  const RenderReplies = ({ replies, discussionId }) => {
    return replies.map((reply) => (
      <div key={reply.id} className="mb-3">
        <div className="bg-gray-50 rounded-lg p-3 group">
          <div className="flex items-start gap-2">
            <div className="w-6 h-6 rounded-full bg-gradient-to-br from-purple-400 to-pink-500 flex items-center justify-center text-white text-xs font-semibold flex-shrink-0 overflow-hidden">
              {reply.avatar_url ? (
                <img src={reply.avatar_url} alt={reply.author} className="w-full h-full object-cover"
                  onError={(e) => { e.target.style.display = 'none'; e.target.parentElement.innerHTML = `<span class="text-white text-xs font-semibold">${getInitials(reply.author)}</span>`; }} />
              ) : <span>{getInitials(reply.author)}</span>}
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center justify-between gap-2 mb-1">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-semibold text-zinc-800">{reply.author}</span>
                  <span className="text-xs text-gray-500">• {reply.date}</span>
                </div>
                <button
                  onClick={() => setReplyingTo(replyingTo === `reply-${discussionId}-${reply.id}` ? null : `reply-${discussionId}-${reply.id}`)}
                  className="opacity-0 group-hover:opacity-100 flex items-center gap-1 text-teal-500 hover:text-teal-600 transition-all text-xs"
                >
                  <CornerDownRight className="w-3 h-3" /><span>Reply</span>
                </button>
              </div>
              {reply.replyTo && (
                <div className="flex items-center gap-1 mb-1">
                  <CornerDownRight className="w-3 h-3 text-gray-400" />
                  <span className="text-xs text-gray-500">Replying to <span className="font-semibold text-teal-600">{reply.replyTo}</span></span>
                </div>
              )}
              <p className="text-sm text-zinc-600 leading-relaxed">{reply.content}</p>
            </div>
          </div>
        </div>

        {replyingTo === `reply-${discussionId}-${reply.id}` && (
          <div className="mt-2 ml-8 bg-purple-50 rounded-lg p-3 border border-purple-100">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-semibold text-purple-700">Reply to {reply.author}</span>
              <button onClick={() => setReplyingTo(null)} className="text-gray-400 hover:text-gray-600"><X className="w-4 h-4" /></button>
            </div>
            <div className="flex gap-2">
              <input type="text" value={replyText} onChange={(e) => setReplyText(e.target.value)}
                placeholder={`Reply to ${reply.author}...`}
                className="flex-1 h-10 px-3 bg-white rounded-lg border border-gray-200 text-sm focus:outline-none focus:border-purple-400"
                onKeyPress={(e) => e.key === 'Enter' && handleReply(discussionId, reply.author, reply.id)} autoFocus />
              <button onClick={() => handleReply(discussionId, reply.author, reply.id)} disabled={!replyText.trim()}
                className="h-10 w-10 bg-purple-500 rounded-lg flex items-center justify-center hover:bg-purple-600 transition-colors disabled:bg-gray-300 disabled:cursor-not-allowed">
                <Send className="w-4 h-4 text-white" />
              </button>
            </div>
          </div>
        )}
      </div>
    ));
  };

  return (
    <div className="flex flex-col h-full bg-white rounded-lg border border-gray-200">
      {/* Header */}
      <div className="border-b border-gray-200 p-4 flex-shrink-0">
        <div className="flex items-center gap-4">
          <h2 className="text-xl font-bold text-zinc-800"></h2>
          <div className="relative flex-1 max-w-xs">
            <select value={selectedSubject} onChange={(e) => setSelectedSubject(e.target.value)}
              disabled={loadingCourses}
              className="w-full h-10 px-4 pr-10 bg-white rounded border border-gray-300 text-sm focus:outline-none focus:border-teal-400 appearance-none disabled:bg-gray-100 cursor-pointer"
              style={{
                backgroundImage: "url('data:image/svg+xml,%3Csvg xmlns=\\'http://www.w3.org/2000/svg\\' width=\\'10\\' height=\\'6\\' viewBox=\\'0 0 10 6\\'%3E%3Cpath fill=\\'%23a1a1a1\\' d=\\'M5 6L0 0h10z\\'/%3E%3C/svg%3E')",
                backgroundRepeat: 'no-repeat', backgroundPosition: 'right 12px center', backgroundSize: '10px'
              }}>
              <option value="">{loadingCourses ? 'Loading Program...' : 'Select Program'}</option>
              {courses.map((course) => (
                <option key={course.course_id} value={course.course_id}>{course.title}</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Discussions */}
      <div ref={scrollContainerRef} className="flex-1 overflow-y-auto p-4" style={{ scrollBehavior: 'smooth' }}>
        {loadingPosts ? (
          <div className="rounded-lg border border-gray-200 p-8 text-center text-gray-500">Loading discussions...</div>
        ) : discussions.length === 0 ? (
          <div className="rounded-lg border border-gray-200 p-8 text-center text-gray-500">
            {selectedSubject ? "No discussions yet. Start one below!" : "Select a program to view discussions"}
          </div>
        ) : (
          <div className="space-y-4">
            {discussions.map((d) => (
              <div key={d.id} className="bg-gray-50 rounded-lg border border-gray-200 p-6">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-teal-400 to-blue-500 flex items-center justify-center text-white font-semibold text-sm overflow-hidden">
                      {d.avatar_url ? (
                        <img src={d.avatar_url} alt={d.author} className="w-full h-full object-cover"
                          onError={(e) => { e.target.style.display = 'none'; e.target.parentElement.innerHTML = `<span class="text-white font-semibold text-sm">${getInitials(d.author)}</span>`; }} />
                      ) : <span>{getInitials(d.author)}</span>}
                    </div>
                    <div className="text-sm font-bold text-gray-800">{d.author}</div>
                  </div>
                  <button onClick={() => setReplyingTo(replyingTo === `main-${d.id}` ? null : `main-${d.id}`)}
                    className="text-teal-500 hover:text-teal-600">
                    <Reply className="w-5 h-5" />
                  </button>
                </div>

                <p className="text-sm text-gray-700 leading-6 mb-4">{d.content}</p>

                {/* ✅ Always PDF icon, left-aligned filename */}
                {d.document && d.attachment_url && (
                  <div className="flex items-center gap-3 p-3 bg-white rounded border border-gray-200 w-64 mb-4">
                    <div className="w-12 h-11 bg-teal-500 rounded flex items-center justify-center flex-shrink-0">
                      <span className="text-white font-bold text-xs">PDF</span>
                    </div>
                    <div className="flex-1 min-w-0 text-left">
                      <div className="text-sm font-medium text-teal-500 truncate text-left" title={d.document}>
                        {d.document}
                      </div>
                      <div className="text-xs text-gray-500 text-left">PDF File</div>
                    </div>
                    <button onClick={() => handleDownload(d.attachment_url, d.document)}
                      className="text-teal-500 hover:text-teal-600 cursor-pointer transition-colors flex-shrink-0"
                      title="Download file">
                      <Download className="w-5 h-5" />
                    </button>
                  </div>
                )}

                {replyingTo === `main-${d.id}` && (
                  <div className="mt-3 mb-4 bg-teal-50 rounded-lg p-3 border border-teal-100">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-semibold text-teal-700">Reply to {d.author}</span>
                      <button onClick={() => setReplyingTo(null)} className="text-gray-400 hover:text-gray-600"><X className="w-4 h-4" /></button>
                    </div>
                    <div className="flex gap-2">
                      <input type="text" value={replyText} onChange={(e) => setReplyText(e.target.value)}
                        placeholder="Write your reply..."
                        className="flex-1 h-10 px-3 bg-white rounded-lg border border-gray-200 text-sm focus:outline-none focus:border-teal-400"
                        onKeyPress={(e) => e.key === 'Enter' && handleReply(d.id, null, null)} autoFocus />
                      <button onClick={() => handleReply(d.id, null, null)} disabled={!replyText.trim()}
                        className="h-10 w-10 bg-teal-500 rounded-lg flex items-center justify-center hover:bg-teal-600 transition-colors disabled:bg-gray-300">
                        <Send className="w-4 h-4 text-white" />
                      </button>
                    </div>
                  </div>
                )}

                {d.replies.length > 0 && (
                  <div className="mt-4 space-y-3 border-l-2 border-teal-200 pl-4">
                    <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
                      {d.replies.length} {d.replies.length === 1 ? 'Reply' : 'Replies'}
                    </div>
                    <RenderReplies replies={d.replies} discussionId={d.id} />
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
        <div ref={chatEndRef} />
      </div>

      {/* Input Box */}
      {selectedSubject && (
        <div className="border-t border-gray-200 p-3 flex-shrink-0 bg-white">
          <div className="flex items-end gap-2">
            <input type="file" ref={fileInputRef} onChange={handleFileSelect} style={{ display: 'none' }}
              accept=".pdf,.doc,.docx,.txt,.png,.jpg,.jpeg,.gif,.webp,.xls,.xlsx,.ppt,.pptx" />
            <button onClick={() => fileInputRef.current?.click()} disabled={uploadingFile}
              className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center hover:bg-gray-200 transition-colors flex-shrink-0 disabled:opacity-50 disabled:cursor-not-allowed">
              <Paperclip className="w-5 h-5 text-zinc-500" />
            </button>

            <div className="flex-1 bg-gray-100 rounded-3xl px-4 py-2">
              {selectedFile && (
                <div className="flex items-center gap-2 mb-2 text-xs text-teal-600">
                  <span> {selectedFile.name}</span>
                  <button onClick={() => { setSelectedFile(null); if (fileInputRef.current) fileInputRef.current.value = ''; }}
                    className="text-red-500 hover:text-red-700"><X className="w-3 h-3" /></button>
                </div>
              )}
              <textarea ref={textareaRef} value={discussionText} onChange={(e) => setDiscussionText(e.target.value)}
                placeholder="Type a message..."
                className="flex-1 w-full bg-transparent text-sm text-zinc-800 placeholder-zinc-500 resize-none focus:outline-none overflow-y-auto"
                rows="1" onInput={handleTextareaInput}
                onKeyPress={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handlePost(); } }}
                style={{ maxHeight: '120px' }} />
            </div>

            <button onClick={handlePost} disabled={(!discussionText.trim() && !selectedFile) || uploadingFile}
              className="w-10 h-10 bg-teal-500 rounded-full flex items-center justify-center hover:bg-teal-600 transition-all disabled:bg-gray-300 flex-shrink-0 hover:scale-105">
              {uploadingFile
                ? <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                : <Send className="w-5 h-5 text-white" />}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}